import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowRight,
    BookOpen,
    Download,
    ExternalLink,
    FileText,
    HelpCircle,
    Loader2,
    Video,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import SectionHeading from "@/components/site/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Placeholder content shown ONLY when the CMS returns nothing ----
const PLACEHOLDER_ARTICLES = [
    { id: "p1", title: "The Six HUMAN+ Pillars, In Plain Language", read_time: "6 min read", subtitle: "A short, executive-friendly walkthrough of why these six capabilities determine whether AI strengthens or hollows your organization." },
    { id: "p2", title: "What Responsible AI Adoption Actually Looks Like", read_time: "8 min read", subtitle: "Beyond policy decks — the behaviors and accountabilities that quietly separate organizations that use AI well from organizations that just buy it." },
    { id: "p3", title: "Human Flourishing Is Not a Wellness Program", read_time: "5 min read", subtitle: "Why most employee well-being initiatives miss the deeper conditions that actually predict sustained performance." },
];
const PLACEHOLDER_RESEARCH = [
    { id: "pr1", title: "Why Mindset for Adaptability Predicts AI Success", read_time: "Working paper" },
    { id: "pr2", title: "Trust, Empathy, and Decision Quality in AI-Augmented Teams", read_time: "Working paper" },
];
const PLACEHOLDER_VIDEOS = [
    { id: "pv1", title: "Introducing the HUMAN+ Framework™", read_time: "4 min" },
    { id: "pv2", title: "AI Partnership: Keeping the Human in the Loop", read_time: "7 min" },
];
const PLACEHOLDER_DOWNLOADS = [
    { id: "pd1", title: "HUMAN+ Framework™ — One-Page Reference", subtitle: "Executive summary of all six pillars on a single page.", file_url: "/downloads/HUMAN-Framework-One-Pager.pdf", filename: "HUMAN-Framework-One-Pager.pdf" },
    { id: "pd2", title: "The HUMAN+ Framework™ Explained", subtitle: "The full deep-dive: definitions, research foundations, and application.", file_url: "/downloads/The-HUMAN-Framework-Explained.pdf", filename: "The-HUMAN-Framework-Explained.pdf" },
    { id: "pd3", title: "30-Day Action Guide — Overview", subtitle: "How the applied phase of the Foundations Experience™ works.", file_url: null },
];
const PLACEHOLDER_FAQS = [
    { id: "f1", title: "Who is The Intuitus Hub™ for?", body: "Executive decision-makers and leadership teams adopting AI — including CEOs, HR/L&D leaders, hospital administrators, university presidents, agency directors, and consultants supporting them." },
    { id: "f2", title: "Do we have to take the Snapshot before working with you?", body: "Not required, but strongly recommended. The Snapshot gives both teams a shared, evidence-based starting point and dramatically accelerates engagement." },
    { id: "f3", title: "Is the HUMAN+ Framework™ research-based?", body: "Yes. It is informed by published work in cognitive science, organizational development, and responsible AI adoption. It is also designed to be operationally usable, not only academically defensible." },
    { id: "f4", title: "How long is the Foundations Experience™?", body: "Most cohorts run as a focused multi-week program with a 30-day applied phase. We confirm dates per cohort during onboarding." },
    { id: "f5", title: "Can we license the framework for internal use?", body: "Yes — certification pathways for internal trainers and consultants are on our roadmap. The fastest path right now is the Foundations Experience™ or a Discovery Call." },
];

// ---- Helpers ----

const withBackend = (url) => {
    if (!url) return url;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
};

const extractYouTubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|v=|shorts\/|embed\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
};

const extractVimeoId = (url) => {
    if (!url) return null;
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
};

const VideoEmbed = ({ url, title }) => {
    const yt = extractYouTubeId(url);
    const vim = extractVimeoId(url);
    if (yt) {
        return (
            <iframe
                title={title}
                className="w-full aspect-video rounded-xl"
                src={`https://www.youtube.com/embed/${yt}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        );
    }
    if (vim) {
        return (
            <iframe
                title={title}
                className="w-full aspect-video rounded-xl"
                src={`https://player.vimeo.com/video/${vim}`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
            />
        );
    }
    return (
        <a href={url} target="_blank" rel="noreferrer" className="ih-btn-secondary mt-3 inline-flex">
            Open video <ExternalLink size={14} />
        </a>
    );
};

const Card = ({ icon: Icon, tag, title, meta, body }) => (
    <article className="ih-card p-7 group relative overflow-hidden">
        <span className="absolute top-0 left-0 h-[3px] w-12 bg-green_brand" aria-hidden="true" />
        <div className="flex items-center gap-2">
            <Icon size={16} className="text-green_brand" />
            <p className="font-poppins font-medium text-green_brand text-[11px] tracking-[0.2em]">{tag}</p>
            {meta && <span className="font-opensans text-[11px] text-[#4A5568] ml-auto">{meta}</span>}
        </div>
        <h3 className="mt-3 font-montserrat font-bold text-indigo_brand text-lg leading-snug">{title}</h3>
        {body && <p className="mt-3 font-opensans text-sm text-[#4A5568] leading-relaxed line-clamp-4">{body}</p>}
    </article>
);

// ---- Main ----

const Resources = () => {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/content`)
            .then((res) => setContent(res.data))
            .catch(() => setContent({ article: [], research: [], video: [], download: [], faq: [] }))
            .finally(() => setLoading(false));
    }, []);

    const articles = useMemo(() => {
        const live = content?.article || [];
        return live.length ? live : PLACEHOLDER_ARTICLES;
    }, [content]);
    const research = useMemo(() => {
        const live = content?.research || [];
        return live.length ? live : PLACEHOLDER_RESEARCH;
    }, [content]);
    const videos = useMemo(() => {
        const live = content?.video || [];
        return live.length ? live : PLACEHOLDER_VIDEOS;
    }, [content]);
    const downloads = useMemo(() => {
        const live = content?.download || [];
        return live.length ? live : PLACEHOLDER_DOWNLOADS;
    }, [content]);
    const faqs = useMemo(() => {
        const live = content?.faq || [];
        return live.length ? live : PLACEHOLDER_FAQS;
    }, [content]);

    return (
        <div data-testid="resources-page">
            <section className="bg-indigo_brand text-white">
                <div className="ih-container py-20 md:py-24">
                    <p className="ih-eyebrow" style={{ color: "#08C020" }}>Resources</p>
                    <h1 className="mt-3 font-montserrat font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                        Ideas worth sharing
                        <span className="block text-green_brand">with your leadership team.</span>
                    </h1>
                    <p className="mt-7 font-opensans text-white/80 text-lg leading-relaxed max-w-2xl">
                        Articles, working papers, short videos, downloads, and answers to the questions executives ask us most.
                    </p>
                </div>
            </section>

            {loading && (
                <div className="py-20 text-center">
                    <Loader2 className="animate-spin mx-auto text-indigo_brand" size={22} />
                </div>
            )}

            {/* Articles */}
            {articles.length > 0 && (
                <section className="ih-section bg-white">
                    <div className="ih-container">
                        <SectionHeading eyebrow="Articles" title="Reading for thoughtful leaders." />
                        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {articles.map((a) => {
                                const inner = (
                                    <Card
                                        icon={FileText}
                                        tag="ARTICLE"
                                        title={a.title}
                                        meta={a.read_time}
                                        body={a.subtitle || a.body}
                                    />
                                );
                                return a.external_url ? (
                                    <a key={a.id} href={a.external_url} target="_blank" rel="noreferrer" className="block" data-testid={`article-${a.id}`}>
                                        {inner}
                                    </a>
                                ) : (
                                    <div key={a.id} data-testid={`article-${a.id}`}>{inner}</div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Research + Videos */}
            {(research.length > 0 || videos.length > 0) && (
                <section className="ih-section bg-surface">
                    <div className="ih-container grid gap-12 lg:grid-cols-2">
                        {research.length > 0 && (
                            <div>
                                <SectionHeading eyebrow="Research" title="Working papers behind the framework." />
                                <div className="mt-10 space-y-4">
                                    {research.map((r) => {
                                        const inner = (
                                            <Card icon={BookOpen} tag="RESEARCH" title={r.title} meta={r.read_time} body={r.subtitle || r.body} />
                                        );
                                        const href = r.file_url ? withBackend(r.file_url) : r.external_url;
                                        return href ? (
                                            <a key={r.id} href={href} target="_blank" rel="noreferrer" className="block" data-testid={`research-${r.id}`}>
                                                {inner}
                                            </a>
                                        ) : (
                                            <div key={r.id} data-testid={`research-${r.id}`}>{inner}</div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {videos.length > 0 && (
                            <div>
                                <SectionHeading eyebrow="Videos" title="Short, executive-paced." />
                                <div className="mt-10 space-y-6">
                                    {videos.map((v) => (
                                        <article key={v.id} data-testid={`video-${v.id}`} className="ih-card p-5 space-y-4">
                                            <div className="flex items-center gap-2">
                                                <Video size={16} className="text-green_brand" />
                                                <p className="font-poppins font-medium text-green_brand text-[11px] tracking-[0.2em]">VIDEO</p>
                                                {v.read_time && <span className="font-opensans text-[11px] text-[#4A5568] ml-auto">{v.read_time}</span>}
                                            </div>
                                            <h3 className="font-montserrat font-bold text-indigo_brand text-lg leading-snug">{v.title}</h3>
                                            {v.external_url ? (
                                                <VideoEmbed url={v.external_url} title={v.title} />
                                            ) : v.subtitle ? (
                                                <p className="font-opensans text-sm text-[#4A5568]">{v.subtitle}</p>
                                            ) : null}
                                        </article>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Downloads */}
            {downloads.length > 0 && (
                <section className="ih-section bg-white">
                    <div className="ih-container">
                        <SectionHeading eyebrow="Downloads" title="Practical, shareable, ready-to-use." />
                        <div className="mt-12 grid gap-6 md:grid-cols-3">
                            {downloads.map((d) => {
                                const href = d.file_url ? withBackend(d.file_url) : null;
                                return (
                                    <article key={d.id} className="ih-card p-7 group flex flex-col" data-testid={`download-${d.id}`}>
                                        <Download size={18} className="text-green_brand" />
                                        <p className="font-poppins font-medium text-green_brand text-[11px] tracking-[0.2em] mt-3">
                                            {href ? "DOWNLOAD · PDF" : "COMING SOON"}
                                        </p>
                                        <h3 className="mt-2 font-montserrat font-bold text-indigo_brand text-base leading-snug">{d.title}</h3>
                                        {(d.subtitle || d.body) && (
                                            <p className="mt-2 font-opensans text-sm text-[#4A5568] leading-relaxed">
                                                {d.subtitle || d.body}
                                            </p>
                                        )}
                                        <div className="mt-auto pt-5">
                                            {href ? (
                                                <a href={href} target="_blank" rel="noreferrer" download className="ih-btn-ghost">
                                                    Download PDF <ArrowRight size={14} />
                                                </a>
                                            ) : (
                                                <span className="font-opensans text-xs text-[#4A5568]">Coming soon</span>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* FAQs */}
            {faqs.length > 0 && (
                <section className="ih-section bg-surface">
                    <div className="ih-container grid gap-12 lg:grid-cols-[1fr_1.6fr]">
                        <div>
                            <SectionHeading eyebrow="FAQ" title="Answers to the questions executives ask first." />
                            <HelpCircle size={28} className="text-green_brand mt-6" />
                        </div>
                        <Accordion type="single" collapsible className="w-full" data-testid="resources-faq">
                            {faqs.map((f, i) => (
                                <AccordionItem key={f.id} value={`faq-${i}`} className="border-b border-surface-border/60">
                                    <AccordionTrigger className="font-montserrat font-bold text-indigo_brand text-base sm:text-lg text-left hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                                        {f.title}
                                    </AccordionTrigger>
                                    <AccordionContent className="font-opensans text-[#4A5568] text-sm leading-relaxed pb-6 whitespace-pre-line">
                                        {f.body || f.subtitle}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </section>
            )}

            <section className="ih-section bg-white">
                <div className="ih-container">
                    <div className="ih-card p-10 md:p-14 grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center shadow-medium">
                        <div>
                            <p className="ih-eyebrow">Start Here</p>
                            <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl leading-tight">
                                Get your organization&rsquo;s HUMAN+ Readiness score.
                            </h2>
                        </div>
                        <div className="flex flex-col gap-3">
                            <Link to="/snapshot" className="ih-btn-primary">Take the FREE Snapshot <ArrowRight size={16} /></Link>
                            <Link to="/discovery" className="ih-btn-secondary">Or schedule a Discovery Call</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Resources;
